import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { useAdminPagedQuery, useAdminQuery } from '@/features/admin/api'
import type { Candidate, QrTarget } from '@/features/admin/types'
import { cn } from '@/lib/cn'

import type { ConductorContest } from '../types'

/** Un libellé multilingue, avec repli sur la première langue renseignée. */
export function text(value: Record<string, string> | null | undefined, locale: string): string {
  if (!value) return '—'
  return value[locale] || Object.values(value).find(Boolean) || '—'
}

/** Le titre d'une section du cockpit, et sa phrase d'aide. */
export function SectionHead({ title, hint }: { title: string; hint?: string | undefined }) {
  return (
    <div>
      <h2 className="font-display text-heading-md text-content-primary">{title}</h2>
      {hint ? <p className="mt-1 max-w-2xl text-sm text-content-secondary">{hint}</p> : null}
    </div>
  )
}

/**
 * Choisir le concours (Miss, Master…) dont on parle.
 *
 * Un seul concours à la fois à l'écran : sur un téléphone, deux tableaux
 * privés l'un sous l'autre, c'est annoncer le mauvais nom.
 */
export function ContestPicker({
  contests,
  value,
  onChange,
}: {
  contests: ConductorContest[]
  value: string | null
  onChange: (id: string) => void
}) {
  const { t, i18n } = useTranslation()
  if (contests.length < 2) return null
  return (
    <div role="group" aria-label={t('conductor.cockpit.pickContest')} className="flex flex-wrap gap-2">
      {contests.map((contest) => (
        <button
          key={contest.id}
          type="button"
          aria-pressed={value === contest.id}
          onClick={() => onChange(contest.id)}
          className={cn(
            'min-h-10 rounded-full border px-4 py-2 text-sm transition',
            value === contest.id
              ? 'border-champagne-400/60 bg-champagne-400/10 text-champagne-300'
              : 'border-line-subtle text-content-secondary hover:text-content-primary',
          )}
        >
          {text(contest.name_i18n, i18n.language)}
        </button>
      ))}
    </div>
  )
}

/** Le concours retenu : celui choisi, sinon le premier. */
export function useContestChoice(contests: ConductorContest[]) {
  const [chosen, setChosen] = useState<string | null>(null)
  const current = contests.find((contest) => contest.id === chosen) ?? contests[0] ?? null
  return [current, setChosen] as const
}

/**
 * Les candidatures de l'édition, toutes pages lues.
 *
 * Clé propre au conducteur : le back-office range sous `admin-candidates` une
 * autre forme de réponse, et partager la clé mélangerait les deux.
 */
export function useCandidates(eventId: string | undefined, enabled: boolean) {
  return useAdminPagedQuery<Candidate>(
    ['conductor-candidates', eventId],
    (page) => `/admin/events/${eventId}/competition/candidates?page=${page}&page_size=200`,
    eventId,
    { enabled },
  )
}

export function candidateName(candidate: Candidate): string {
  return [
    candidate.candidate_number ? `${candidate.candidate_number}.` : null,
    candidate.stage_name ?? `${candidate.first_name} ${candidate.last_name}`,
  ]
    .filter(Boolean)
    .join(' ')
}

/** Les liens et QR de la soirée, tels que le serveur les compose. */
export function useQrTargets(eventId: string | undefined, enabled: boolean) {
  return useAdminQuery<QrTarget[]>(
    ['conductor-qr', eventId],
    `/admin/events/${eventId}/qr-codes`,
    eventId,
    { enabled, retry: false },
  )
}

/**
 * Un lien à donner à la salle : l'adresse, la copier, et son QR en grand —
 * à la demande, pour le tendre ou le filmer pour l'écran.
 */
export function LinkWithQr({
  title,
  hint,
  target,
  extra,
}: {
  title: string
  hint?: string | undefined
  target: QrTarget
  extra?: ReactNode
}) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)

  const copy = () => {
    // Le presse-papiers exige une page sûre (https) ; à défaut, l'adresse
    // reste lisible et sélectionnable juste au-dessus.
    void navigator.clipboard
      ?.writeText(target.url)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => setCopied(false))
  }

  return (
    <div className="rounded-md border border-line-subtle px-3 py-3">
      <p className="text-sm text-content-primary">{title}</p>
      {hint ? <p className="mt-0.5 text-caption text-content-muted">{hint}</p> : null}
      <p className="mt-1 break-all font-mono text-caption text-content-secondary">{target.url}</p>
      {extra}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setShown((value) => !value)}>
          {shown ? t('conductor.cockpit.hideQr') : t('conductor.cockpit.showQr')}
        </Button>
        <Button size="sm" variant="ghost" onClick={copy}>
          {copied ? t('conductor.cockpit.copied') : t('conductor.cockpit.copy')}
        </Button>
      </div>
      {shown ? (
        <div className="mt-3 flex justify-center">
          <div className="w-full max-w-[16rem] rounded-md bg-white p-3">
            <img src={target.svg} alt={title} className="h-auto w-full" />
          </div>
        </div>
      ) : null}
    </div>
  )
}
